import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@workingclock/i18n';
import { Check, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu';

export interface LanguageToggleProps {
  /** Accessible label for the trigger button; defaults to "Change language". */
  triggerLabel?: string;
  className?: string;
}

/**
 * Language dropdown — the i18n counterpart of ThemeToggle. Reads/writes the
 * current app's i18n instance directly (via react-i18next context), so
 * switching persists through libs/i18n's own `languageChanged` listener.
 */
export function LanguageToggle({ triggerLabel = 'Change language', className }: LanguageToggleProps) {
  const { i18n } = useTranslation();
  const current = i18n.language as SupportedLanguage;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-bg text-ink',
            'transition-colors duration-150 hover:bg-surface motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            className,
          )}
        >
          <Languages className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onSelect={() => void i18n.changeLanguage(lang)}
            className="justify-between gap-6"
          >
            <span>{LANGUAGE_LABELS[lang]}</span>
            {current === lang && <Check className="h-4 w-4 text-gold" aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
